import type { ConfiguratorOriginsType } from '@susu/custom-made/utils/constants';
import { ConfiguratorOrigin } from '@susu/custom-made/utils/constants';
import type { TranslationFunction } from '@susu/i18n';

// Query params that make the configurator load a *saved* configuration instead of
// a default one. All of them have to go for "start again" to actually start again:
// `preloadConfiguration` is a fallback source for `configurationId` and also forces
// origin=Clone, and `shareCode` is an independent lookup key — leaving either one
// re-runs the exact request that just failed.
const savedConfigurationParams = ['configurationId', 'preloadConfiguration', 'shareCode', 'subconfigurationId', 'origin'];

// The configurator is mounted at /configurator standalone, but at /{locale}/custom-made
// when embedded by the webstore, so the current pathname is the only correct base to
// redirect back to — a hardcoded /configurator 404s in every embedded host.
const replaceUrl = (mutateParams: (params: URLSearchParams) => void) => {
  const { origin, pathname, search } = location;
  const redirectUrl = new URL(`${pathname}${search}`, origin);
  mutateParams(redirectUrl.searchParams);

  location.replace(redirectUrl.toString());
};

const startAgain = () =>
  replaceUrl(queryString => {
    queryString.delete('customError');
    savedConfigurationParams.forEach(param => queryString.delete(param));
  });

const redirectToReorderPage = () => replaceUrl(queryString => queryString.set('reorder', 'true'));

const redirectToSearchPage = () => replaceUrl(queryString => queryString.set('search', 'true'));

export type FetchError = {
  title: string;
  description: string;
  buttonText: string;
  handler: () => void;
};

export enum FetchErrors {
  None = 'None',
  General = 'General',
  ReorderNotFound = 'ReorderNotFound',
  SaveForLaterNotFoundShareCode = 'SaveForLaterNotFoundShareCode',
  SaveForLaterNotFoundConfigurationId = 'SaveForLaterNotFoundConfigurationId',
  FinishInStoreNotFoundShareCode = 'FinishInStoreNotFoundShareCode',
  FinishInStoreNotFoundConfigurationId = 'FinishInStoreNotFoundConfigurationId',
  CloneNotFound = 'CloneNotFound',
}

export type FetchErrorsType = keyof typeof FetchErrors;

export const getFetchErrors = (t: TranslationFunction): { [key: string]: FetchError } => ({
  General: {
    title: t('fetchErrors.somethingWentWrong'),
    description: t('fetchErrors.somethingWentWrongDescription'),
    buttonText: t('fetchErrors.startAgain'),
    handler: startAgain,
  },
  ReorderNotFound: {
    title: t('fetchErrors.wrongConfigId'),
    description: t('fetchErrors.wrongConfigIdDescription'),
    buttonText: t('fetchErrors.tryAgain'),
    handler: redirectToReorderPage,
  },
  SaveForLaterNotFoundShareCode: {
    title: t('fetchErrors.wrongShareCode'),
    description: t('fetchErrors.wrongShareCodeDescription'),
    buttonText: t('fetchErrors.tryAgain'),
    handler: redirectToSearchPage,
  },
  // Handler is different because this could happen only in onLine flow
  SaveForLaterNotFoundConfigurationId: {
    title: t('fetchErrors.wrongConfigId'),
    description: t('fetchErrors.wrongConfigIdDescription'),
    buttonText: t('fetchErrors.tryAgain'),
    handler: startAgain,
  },
  FinishInStoreNotFoundShareCode: {
    title: t('fetchErrors.wrongShareCode'),
    description: t('fetchErrors.wrongShareCodeDescription'),
    buttonText: t('fetchErrors.tryAgain'),
    handler: redirectToSearchPage,
  },
  FinishInStoreNotFoundConfigurationId: {
    title: t('fetchErrors.wrongConfigId'),
    description: t('fetchErrors.wrongConfigIdDescription'),
    buttonText: t('fetchErrors.tryAgain'),
    handler: redirectToSearchPage,
  },
  CloneNotFound: {
    title: t('fetchErrors.wrongConfigId'),
    description: t('fetchErrors.wrongConfigIdDescription'),
    buttonText: t('fetchErrors.restartConfigurator'),
    handler: startAgain,
  },
});

export const determineErrorType = (
  isNotFoundError: boolean,
  origin: ConfiguratorOriginsType,
  shareCode?: string,
): FetchErrorsType => {
  if (!isNotFoundError) {
    return FetchErrors.General;
  }

  switch (origin) {
    case ConfiguratorOrigin.FinishInStore:
      return shareCode ? FetchErrors.FinishInStoreNotFoundShareCode : FetchErrors.FinishInStoreNotFoundConfigurationId;
    case ConfiguratorOrigin.SaveForLater:
      return shareCode ? FetchErrors.SaveForLaterNotFoundShareCode : FetchErrors.SaveForLaterNotFoundConfigurationId;
    case ConfiguratorOrigin.Reorder:
      return FetchErrors.ReorderNotFound;
    case ConfiguratorOrigin.Clone:
      return FetchErrors.CloneNotFound;
    default:
      return FetchErrors.General;
  }
};
